
import React from 'react';
import { FieldsList } from './FieldsList';
import { SignupFormConfig, SignupFormField } from '@/schemas/signupFormSchema';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import EmailSettings from './EmailSettings';
import ValidationSettings from './ValidationSettings';
import TimestampSetting from './TimestampSetting';
import AddFieldSection from './AddFieldSection';
import FormPreview from './FormPreview';
import { v4 as uuidv4 } from 'uuid';

interface FormSettingsSectionProps {
  formConfig: SignupFormConfig;
  setFormConfig: React.Dispatch<React.SetStateAction<SignupFormConfig>>;
  onSave: () => void;
  isSaving: boolean;
}

const FormSettingsSection: React.FC<FormSettingsSectionProps> = ({
  formConfig,
  setFormConfig,
  onSave,
  isSaving
}) => {
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormConfig(prev => ({ ...prev, formTitle: e.target.value }));
  };

  const handleFieldUpdate = (updatedField: SignupFormField) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.map(field => 
        field.id === updatedField.id ? updatedField : field
      )
    }));
  };

  const handleFieldDelete = (fieldId: string) => {
    setFormConfig(prev => ({
      ...prev,
      fields: prev.fields.filter(field => field.id !== fieldId)
    }));
  };

  const handleAddField = (field: SignupFormField) => {
    // Ensure the field has a unique ID
    const newField = { ...field, id: uuidv4() };
    setFormConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="formTitle">Form Title</Label>
        <Input 
          id="formTitle" 
          value={formConfig.formTitle} 
          onChange={handleTitleChange} 
          placeholder="Enter form title" 
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Form Fields</h3>
                <AddFieldSection onAddField={handleAddField} />
              </div>
              <FieldsList 
                fields={formConfig.fields}
                onFieldUpdate={handleFieldUpdate}
                onFieldDelete={handleFieldDelete}
              />
            </CardContent>
          </Card>

          <div className="space-y-4">
            <EmailSettings 
              formConfig={formConfig}
              setFormConfig={setFormConfig}
            />
            
            <ValidationSettings 
              formConfig={formConfig}
              setFormConfig={setFormConfig}
            />
            
            <TimestampSetting 
              formConfig={formConfig}
              setFormConfig={setFormConfig}
            />
          </div>
        </div>
        
        <div>
          <FormPreview formConfig={formConfig} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Form Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default FormSettingsSection;
